import {IntegrationInterface} from "../../app/shared/interfaces/integrations/integration.interface";

export interface StandardPricingRow {
  service: string
  cost: string
  optional?: boolean
  included?: string   // optional 3rd column (e.g. Google Places "What's included")
}

export interface BuiltInSourceConfig extends IntegrationInterface {
  infoTitle?: string           // modal header label (defaults to name)
  infoDescription: string[]   // paragraphs shown before pricing table
  pricingLabel?: string        // 1st column header of pricing table, defaults to "Service"
  pricingRows?: StandardPricingRow[]
  pricingNotes?: string[]      // paragraphs shown after pricing table
  poweredBy?: { name: string; url: string; logo: string }
  costKey?: string             // key in AdditionalCostsInterface (when different from type)
  priceHeadline?: string  // Text like: "From 0.005€ per email", or "0.35€/phone number"
  category: 'validation' | 'enrichment' | 'scraping' | 'utility'
  /** Not pay-per-use, so it isn't gated behind `workspace.builtInIntegrations` like the rest.
   *  Set only for sources that read data the workspace already owns. */
  free?: boolean
}

export const builtInSources: BuiltInSourceConfig[] = [
  {
    id: 'datamorf',
    type: 'datamorf',
    name: 'Onion workflow',
    infoTitle: 'Onion workflow',
    infoDescription: [
      'Trigger another Onion workflow with ease. This integration allows you to chain multiple workflows together, enabling you to build complex data processing pipelines.',
      'This is useful when you need to perform multiple operations on the same data, or when you need to enrich the data further using different services.'
    ],
    endpoint: '',
    description: '',
    createdBy: 'system',
    createdAt: new Date().getTime(),
    lastUpdate: -1,
    lastUpdateBy: '',
    headers: [],
    bodyConstants: [],
    logo: '',
    category: 'utility'
  },
  {
    id: 'zerobounce-datamorf',
    type: 'zerobounce-datamorf',
    name: 'ZeroBounce',
    infoTitle: 'Zero Bounce email validator',
    infoDescription: ['Powerful email validation out of the box.'],
    pricingRows: [{ service: 'Email validation', cost: '€0.009 per email' }],
    poweredBy: { name: 'ZeroBounce', url: 'https://zerobounce.com', logo: './assets/img/vendors/zerobounce-datamorf.png' },
    costKey: 'zerobounce',
    endpoint: '',
    description: '',
    createdBy: 'system',
    createdAt: new Date().getTime(),
    lastUpdate: -1,
    lastUpdateBy: '',
    headers: [],
    bodyConstants: [],
    logo: '',
    additionalCost: true,
    priceHeadline: "€0.009",
    category: 'validation'
  },
  {
    id: 'linkedin-scraper',
    type: 'linkedin-scraper',
    name: 'LinkedIn',
    infoTitle: 'LinkedIn scraper',
    infoDescription: ['Scrape a LinkedIn page to extract the public data in it.'],
    pricingRows: [
      { service: 'Person profile', cost: '€0.006' },
      { service: 'Company page', cost: '€0.006' },
      { service: 'Person posts', cost: '€0.004 per post' },
      { service: 'Post content', cost: '€0.025' },
      { service: 'Post comments', cost: '€0.004 per comment' },
      { service: 'Post comments with profile details', cost: '€0.009 per comment' },
    ],
    costKey: 'linkedin-scraper',
    endpoint: '',
    description: '',
    createdBy: 'system',
    createdAt: new Date().getTime(),
    lastUpdate: -1,
    lastUpdateBy: '',
    headers: [],
    bodyConstants: [],
    logo: '',
    additionalCost: true,
    priceHeadline: "From €0.004",
    category: 'scraping'
  },
  {
    id: 'instagram',
    type: 'instagram-scraper',
    name: 'Instagram',
    infoTitle: 'Instagram scraper',
    infoDescription: ['Scrape an Instagram page to extract all the public data in it.'],
    pricingRows: [
      { service: 'Profile scraper', cost: '€0.004 per scrape' },
    ],
    costKey: 'instagram-scraper',
    endpoint: '',
    description: '',
    createdBy: 'system',
    createdAt: new Date().getTime(),
    lastUpdate: -1,
    lastUpdateBy: '',
    headers: [],
    bodyConstants: [],
    logo: '',
    additionalCost: true,
    priceHeadline: '€0.004',
    category: 'scraping'
  },
  {
    id: 'million-verifier-datamorf',
    type: 'million-verifier-datamorf',
    name: 'Million Verifier',
    infoTitle: 'Million Verifier email validator',
    infoDescription: ['Cost efficient email validation out of the box.'],
    pricingRows: [{ service: 'Email validation', cost: '€0.001 per email' }],
    poweredBy: { name: 'MillionVerifier', url: 'https://millionverifier.com', logo: './assets/img/vendors/million-verifier-datamorf.png' },
    costKey: 'millionVerifier',
    endpoint: '',
    description: '',
    createdBy: 'system',
    createdAt: new Date().getTime(),
    lastUpdate: -1,
    lastUpdateBy: '',
    headers: [],
    bodyConstants: [],
    logo: '',
    additionalCost: true,
    priceHeadline: '€0.001',
    category: 'validation'
  },
  {
    id: 'bounceban-datamorf',
    type: 'bounceban-datamorf',
    name: 'Bounce Ban',
    infoTitle: 'Bounce Ban email validator',
    infoDescription: ['Catch-all email validation out of the box.'],
    pricingRows: [{ service: 'Email validation', cost: '€0.004 per email' }],
    poweredBy: { name: 'Bounce Ban', url: 'https://bounceban.com/?via=datamorf', logo: './assets/img/vendors/bounceban-datamorf.png' },
    costKey: 'bounceBan',
    endpoint: '',
    description: '',
    createdBy: 'system',
    createdAt: new Date().getTime(),
    lastUpdate: -1,
    lastUpdateBy: '',
    headers: [],
    bodyConstants: [],
    logo: '',
    additionalCost: true,
    priceHeadline: '€0.004',
    category: 'validation'
  },
  {
    id: 'similar-web-analytics',
    type: 'similar-web-analytics',
    name: 'Similar Web',
    infoTitle: 'Similar web analytics',
    infoDescription: ['Similar web traffic analytics out of the box.'],
    pricingRows: [{ service: 'Domain analytics', cost: '€0.005 per domain' }],
    costKey: 'similar-web-analytics',
    endpoint: '',
    description: '',
    createdBy: 'system',
    createdAt: new Date().getTime(),
    lastUpdate: -1,
    lastUpdateBy: '',
    headers: [],
    bodyConstants: [],
    logo: '',
    additionalCost: true,
    priceHeadline: '€0.005',
    category: 'enrichment'
  },
  {
    id: 'trustpilot-scraper',
    type: 'trustpilot-scraper',
    name: 'Trustpilot',
    infoTitle: 'Trustpilot scraper',
    infoDescription: ['Scrape a Trustpilot profile to extract all public reviews.'],
    pricingRows: [{ service: 'Profile scraper', cost: '€0.005 per review' }],
    costKey: 'trustpilot-scraper',
    endpoint: '',
    description: '',
    createdBy: 'system',
    createdAt: new Date().getTime(),
    lastUpdate: -1,
    lastUpdateBy: '',
    headers: [],
    bodyConstants: [],
    logo: '',
    additionalCost: true,
    priceHeadline: '€0.005',
    category: 'scraping'
  },
  {
    id: 'google-search',
    type: 'google-search',
    name: 'Google Search',
    infoTitle: 'Google search',
    infoDescription: ['Run google searches and get up to 100 results per query.'],
    pricingLabel: 'Request',
    pricingRows: [
      { service: 'Successful', cost: '€0.0075 (per whole search)' },
      { service: 'Not successful', cost: 'No cost' },
    ],
    costKey: 'google-search',
    endpoint: '',
    description: '',
    createdBy: 'system',
    createdAt: new Date().getTime(),
    lastUpdate: -1,
    lastUpdateBy: '',
    headers: [],
    bodyConstants: [],
    logo: '',
    additionalCost: true,
    priceHeadline: '€0.0075',
    category: 'scraping'
  },
  {
    id: 'waterfall-datamorf',
    type: 'waterfall-datamorf',
    name: 'Waterfall.io',
    infoTitle: 'Waterfall.io enrichment',
    infoDescription: [
      'High-quality contact and company enrichment with strong coverage, accuracy, and reliable phone and email resolution.'
    ],
    pricingRows: [
      { service: 'Contact Enrichment', cost: '€0.08 per contact' },
      { service: 'Contact Info (no email)', cost: '€0.04 per contact' },
      { service: 'Company Enrichment', cost: '€0.08 per company' },
      { service: 'Phone Enrichment', cost: '€0.39 per phone number' },
      { service: 'Company Prospecting', cost: '€0.08 per contact found' },
      { service: 'Find people in company', cost: '€0.04 per contact found' },
      { service: 'Email Verifier', cost: '€0.009 per email' },
    ],
    poweredBy: {
      name: 'Waterfall.io',
      url: 'https://waterfall.io',
      logo: './assets/img/vendors/waterfall-datamorf.png'
    },
    pricingNotes: ['Only billed for successful enrichments.'],
    endpoint: '',
    description: '',
    createdBy: 'system',
    createdAt: new Date().getTime(),
    lastUpdate: -1,
    lastUpdateBy: '',
    headers: [],
    bodyConstants: [],
    logo: '',
    additionalCost: true,
    priceHeadline: 'From €0.009',
    category: 'enrichment'
  },
  {
    id: 'google-places-api',
    type: 'google-places-api',
    name: 'Google Places',
    infoTitle: 'Google Places',
    infoDescription: ['Built-in Google Maps official places API.'],
    pricingLabel: 'Request',
    pricingRows: [
      { service: 'Details (id only)', cost: 'Free', included: 'ID, name, attributions, photos, moved place info' },
      { service: 'Details (essential)', cost: '€0.01', included: 'Address, location, types, viewport, plus code' },
      { service: 'Details (pro)', cost: '€0.03', included: 'Essential + display name, business status, icon, maps links, primary type' },
      { service: 'Details (enterprise)', cost: '€0.04', included: 'Pro + phone, website, opening hours, price level, ratings' },
      { service: 'Details (complete)', cost: '€0.05', included: 'Enterprise + atmosphere data (allows dogs, accessibility, amenities), reviews, reviews summary, etc' },
    ],
    costKey: 'google-places-api',
    endpoint: '',
    description: '',
    createdBy: 'system',
    createdAt: new Date().getTime(),
    lastUpdate: -1,
    lastUpdateBy: '',
    headers: [],
    bodyConstants: [],
    logo: '',
    additionalCost: true,
    priceHeadline: 'From €0',
    category: 'enrichment'
  },
  {
    id: 'url-inspector',
    type: 'url-inspector',
    name: 'URL Inspector',
    infoTitle: 'URL inspector',
    infoDescription: [
      'Follows a URL and returns its full request trace, including redirect chain, status codes, final URL and domain. Optionally, it can also return the final page content.',
      'Useful for debugging redirects, validating links, checking landing pages, or extracting metadata before processing.'
    ],
    pricingLabel: 'Inspect',
    pricingRows: [
      { service: 'Basic', cost: '€0.001' },
      { service: 'With web unlocker', cost: '€0.0025' },
    ],
    pricingNotes: [
      'Basic: Performs a standard HTTP request. It is faster and cheaper, and can reliably return redirect chain details and final URL information. However, it may fail on websites protected by IP restrictions, bot detection, CAPTCHAs, or heavy JavaScript rendering.',
      'With web unlocker: Simulates real user navigation in a browser environment. It can bypass most IP blocks, bot protection mechanisms, CAPTCHAs, and fully render JavaScript-based pages. Due to the browser simulation layer, detailed redirect trace data may be limited compared to the Basic mode.'
    ],
    costKey: 'url-inspector',
    endpoint: '',
    description: '',
    createdBy: 'system',
    createdAt: new Date().getTime(),
    lastUpdate: -1,
    lastUpdateBy: '',
    headers: [],
    bodyConstants: [],
    logo: '',
    additionalCost: true,
    priceHeadline: 'From €0.001',
    category: "utility"
  },
  {
    id: 'google-maps-scraper',
    type: 'google-maps-scraper',
    name: 'Google Maps',
    infoTitle: 'Google Maps Scraper',
    infoDescription: ['Extract Google Maps data at scale.'],
    pricingLabel: 'Task',
    pricingRows: [{ service: 'Place', cost: '€0.0075' }],
    costKey: 'google-maps-scraper',
    endpoint: '',
    description: '',
    createdBy: 'system',
    createdAt: new Date().getTime(),
    lastUpdate: -1,
    lastUpdateBy: '',
    headers: [],
    bodyConstants: [],
    logo: '',
    additionalCost: true,
    priceHeadline: '€0.0075',
    category: 'scraping'
  },
  // {
  //   id: 'aviato-datamorf',
  //   type: 'aviato-datamorf',
  //   name: 'Aviato',
  //   infoTitle: 'Aviato data provider',
  //   infoDescription: ['Company and contact enrichment'],
  //   pricingLabel: 'Task',
  //   pricingRows: [
  //     { service: 'Contact Enrichment', cost: '€0.04' },
  //     { service: 'Company Enrichment', cost: '€0.04' },
  //     { service: 'Get email', cost: '€0.025' },
  //   ],
  //   poweredBy: {
  //     name: 'Aviato',
  //     url: 'https://aviato.co',
  //     logo: './assets/img/vendors/aviato-datamorf.png'
  //   },
  //   endpoint: '',
  //   description: '',
  //   createdBy: 'system',
  //   createdAt: new Date().getTime(),
  //   lastUpdate: -1,
  //   lastUpdateBy: '',
  //   headers: [],
  //   bodyConstants: [],
  //   logo: '',
  //   additionalCost: true,
  //   priceHeadline: 'From €0.0025',
  //   category: 'enrichment'
  // },
  {
    id: 'prospeo-datamorf',
    type: 'prospeo-datamorf',
    name: 'Prospeo',
    infoTitle: 'Prospeo enrichment',
    infoDescription: [
      'High-quality contact and company enrichment with strong coverage, accuracy, and reliable phone and email resolution.'
    ],
    pricingRows: [
      { service: 'Contact Enrichment', cost: '€0.05 per contact' },
      { service: 'Company Enrichment', cost: '€0.05 per company' },
      // { service: 'Company Prospecting', cost: '€0.05 per contact found' },
    ],
    poweredBy: {
      name: 'Prospeo',
      url: 'https://prospeo.io/?via=datamorf',
      logo: './assets/img/vendors/prospeo-datamorf.png'
    },
    // pricingNotes: ['Only billed for successful enrichments. Failed enrichments are not charged.'],
    endpoint: '',
    description: '',
    createdBy: 'system',
    createdAt: new Date().getTime(),
    lastUpdate: -1,
    lastUpdateBy: '',
    headers: [],
    bodyConstants: [],
    logo: '',
    additionalCost: true,
    priceHeadline: '€0.05',
    category: 'enrichment'
  },
  // {
  //   id: 'provero-datamorf',
  //   type: 'provero-datamorf',
  //   name: 'UK TCP phone validation',
  //   infoTitle: 'Phone validation (UK only)',
  //   infoDescription: [
  //     'Screen numbers against the UK preference registers before you dial, and steer clear of the fines.'
  //   ],
  //   pricingRows: [
  //     { service: 'TPS / CTPS Screening', cost: '€0.01' },
  //   ],
  //   poweredBy: {
  //     name: 'Provero',
  //     url: 'https://provero.io/?via=datamorf',
  //     logo: './assets/img/vendors/provero-datamorf.png'
  //   },
  //   // pricingNotes: ['Only billed for successful enrichments. Failed enrichments are not charged.'],
  //   endpoint: '',
  //   description: '',
  //   createdBy: 'system',
  //   createdAt: new Date().getTime(),
  //   lastUpdate: -1,
  //   lastUpdateBy: '',
  //   headers: [],
  //   bodyConstants: [],
  //   logo: '',
  //   additionalCost: true,
  //   priceHeadline: '€0.01',
  //   category: 'validation'
  // },
  {
    id: 'tps-check', // https://tpsmydata.co.uk/your-details/
    type: 'tps-check',
    name: 'TPS validation (UK)',
    infoTitle: 'Phone validation (UK only)',
    infoDescription: [
      'Screen numbers against the UK preference registers before you dial, and steer clear of the fines.'
    ],
    pricingRows: [
      { service: 'TPS / CTPS Validation', cost: '€0.01' },
    ],
    // poweredBy: {
    //   name: 'Provero',
    //   url: 'https://provero.io/?via=datamorf',
    //   logo: './assets/img/vendors/provero-datamorf.png'
    // },
    // pricingNotes: ['Only billed for successful enrichments. Failed enrichments are not charged.'],
    endpoint: '',
    description: '',
    createdBy: 'system',
    createdAt: new Date().getTime(),
    lastUpdate: -1,
    lastUpdateBy: '',
    headers: [],
    bodyConstants: [],
    logo: '',
    additionalCost: true,
    priceHeadline: '€0.01',
    category: 'validation'
  },
  {
    id: 'score-company-datamorf',
    type: 'score-company-datamorf',
    name: 'Score company',
    infoTitle: 'Score company against your ICPs',
    infoDescription: [
      'Scores a company against one or more of your ICP definitions in a single call, returning an overall score/tier/rationale plus a full breakdown (score, tier, rationale, matched signals & disqualifiers) for each ICP.',
      'Uses each ICP\'s own scoring instructions, so there\'s nothing to configure beyond which company to score and which ICP(s) — up to 5 — to score it against.'
    ],
    pricingRows: [
      { service: 'First ICP', cost: '€0.05' },
      { service: 'Each additional ICP', cost: '€0.005 per icp', optional: true, included: 'Up to 5 ICPs per call' },
    ],
    costKey: 'score-company',
    endpoint: '',
    description: '',
    createdBy: 'system',
    createdAt: new Date().getTime(),
    lastUpdate: -1,
    lastUpdateBy: '',
    headers: [],
    bodyConstants: [],
    logo: '',
    additionalCost: true,
    priceHeadline: 'From €0.05',
    category: 'enrichment'
  },
  {
    id: 'prospector-onion',
    type: 'prospector-onion',
    name: 'Prospector',
    infoTitle: 'Prospector',
    infoDescription: [
      'Finds contacts at a company that match one of your ICPs. Job titles, seniorities and departments come straight from the ICP definition — there is nothing to re-type here.',
      'Pick whether you also want each contact\'s email address. Contacts are returned without an email on the cheaper operation, so you can qualify a list first and only pay for emails when you actually want to reach out.'
    ],
    pricingRows: [
      { service: 'Find contacts', cost: '€0.03 per contact found' },
      { service: 'Find contacts with email', cost: '€0.05 per contact found' },
    ],
    pricingNotes: ['Only billed for contacts actually found — a search that returns fewer contacts than requested is only charged for what it returns.'],
    costKey: 'prospector',
    endpoint: '',
    description: '',
    createdBy: 'system',
    createdAt: new Date().getTime(),
    lastUpdate: -1,
    lastUpdateBy: '',
    headers: [],
    bodyConstants: [],
    logo: '',
    additionalCost: true,
    priceHeadline: 'From €0.03',
    category: 'enrichment'
  },
  {
    id: 'contact-finder-onion',
    type: 'contact-finder-onion',
    name: 'Contact finder',
    infoTitle: 'Contact finder',
    infoDescription: [
      'Finds the people at a company who match the job titles you enter. Give it a company name, domain or LinkedIn URL — a domain or LinkedIn URL gives the most accurate matches.',
      'Pick whether you also want each contact\'s email address. Contacts are returned without an email on the cheaper operation.'
    ],
    pricingRows: [
      { service: 'Find contacts', cost: '€0.03 per contact found' },
      { service: 'Find contacts with email', cost: '€0.05 per contact found' },
    ],
    pricingNotes: ['Only billed for contacts actually found — a search that returns fewer contacts than requested is only charged for what it returns.'],
    costKey: 'prospector',
    endpoint: '',
    description: '',
    createdBy: 'system',
    createdAt: new Date().getTime(),
    lastUpdate: -1,
    lastUpdateBy: '',
    headers: [],
    bodyConstants: [],
    logo: '',
    additionalCost: true,
    priceHeadline: 'From €0.03',
    category: 'enrichment'
  },
  {
    id: 'enrichment-onion',
    type: 'enrichment-onion',
    name: 'Enrichment',
    infoTitle: 'Enrichment',
    infoDescription: [
      'Enriches a company from its domain or LinkedIn URL: name, industry, size, location, description, technologies and more.',
      'Enriches a contact from their LinkedIn URL or email: role, experience, location and email address.',
      'Every enriched company and contact is added to your CRM.'
    ],
    pricingRows: [
      { service: 'Company enrichment', cost: '€0.04 per company found' },
      { service: 'Contact enrichment', cost: '€0.04 per contact found' },
    ],
    pricingNotes: ['Only billed when enrichment is successfully found.'],
    costKey: 'enrichment',
    endpoint: '',
    description: '',
    createdBy: 'system',
    createdAt: new Date().getTime(),
    lastUpdate: -1,
    lastUpdateBy: '',
    headers: [],
    bodyConstants: [],
    logo: '',
    additionalCost: true,
    priceHeadline: '€0.04',
    category: 'enrichment'
  },
  {
    id: 'crm',
    type: 'crm',
    name: 'CRM',
    infoTitle: 'CRM',
    infoDescription: [
      'Look up contacts, companies and meetings from your CRM in the middle of a workflow. Add filters — they are all combined with AND — and get back the first match or every match.',
      'Nothing to connect and nothing to pay — it is your data.'
    ],
    free: true,
    endpoint: '',
    description: '',
    createdBy: 'system',
    createdAt: new Date().getTime(),
    lastUpdate: -1,
    lastUpdateBy: '',
    headers: [],
    bodyConstants: [],
    logo: '',
    category: 'utility'
  },
]

export const builtInExtractions: BuiltInSourceConfig[] = [
  {
    id: 'google-maps-scraper',
    type: 'google-maps-scraper',
    name: 'Google Maps',
    infoTitle: 'Google Maps Scraper',
    infoDescription: [
      'Search Google Maps for businesses by query and location, and extract each result as structured data ready to use in your workflow.'
    ],
    pricingRows: [
      { service: 'Start extraction fee', cost: '€0.01 (one time)' },
      { service: 'Places', cost: '€0.0075 per place' },
      { service: 'Additional details', cost: '€0.0025 per place', optional: true, included: 'Opening hours, popular times, reviews summary, Q&A, and more' },
      { service: 'Contact details', cost: '€0.0025 per place', optional: true, included: 'Business emails and social profiles (Meta, LinkedIn, X, etc.)' },
      // { service: 'Include reviews', cost: '€0.0007 per review', optional: true },
      // { service: 'Include images', cost: '€0.0007 per image', optional: true },
    ],
    // poweredBy: {
    //   name: 'Provero',
    //   url: 'https://provero.io/?via=datamorf',
    //   logo: './assets/img/vendors/provero-datamorf.png'
    // },
    // pricingNotes: ['Only billed for successful enrichments. Failed enrichments are not charged.'],
    endpoint: '',
    description: '',
    createdBy: 'system',
    createdAt: new Date().getTime(),
    lastUpdate: -1,
    lastUpdateBy: '',
    headers: [],
    bodyConstants: [],
    logo: '',
    additionalCost: true,
    priceHeadline: '€0.0075',
    category: 'scraping'
  },
  {
    id: 'track',
    type: 'track',
    name: 'CRM',
    infoTitle: 'CRM',
    infoDescription: [
      'Pull contacts and companies back out of the data you have in the CRM section, filtered by their own fields and by when things last happened to them. Nothing to connect and nothing to pay — it is your data.',
      'Find the records your campaigns have missed — contacts with a LinkedIn profile never enrolled in a LinkedIn campaign, contacts enrolled but who never replied, companies not scored in six months — and feed them straight into a workflow.'
    ],
    free: true,
    endpoint: '',
    description: '',
    createdBy: 'system',
    createdAt: new Date().getTime(),
    lastUpdate: -1,
    lastUpdateBy: '',
    headers: [],
    bodyConstants: [],
    logo: '',
    category: 'utility'
  },
  {
    id: 'crm-segment',
    type: 'crm-segment',
    name: 'Segments',
    infoTitle: 'Segments',
    infoDescription: [
      'Pull the contacts or companies in one of your CRM segments. The segment is read on every run, so editing it changes what this extraction pulls.'
    ],
    free: true,
    endpoint: '',
    description: '',
    createdBy: 'system',
    createdAt: new Date().getTime(),
    lastUpdate: -1,
    lastUpdateBy: '',
    headers: [],
    bodyConstants: [],
    logo: '',
    category: 'utility'
  },
  {
    id: 'file-upload',
    type: 'file-upload',
    name: 'File Upload',
    infoTitle: 'File Upload',
    infoDescription: [
      'Upload a CSV, Excel, or JSON file and extract its rows into your workflow, just like any other extraction.'
    ],
    endpoint: '',
    description: '',
    createdBy: 'system',
    createdAt: new Date().getTime(),
    lastUpdate: -1,
    lastUpdateBy: '',
    headers: [],
    bodyConstants: [],
    logo: '',
    category: 'utility'
  },
]
